import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Spin, Tag, Timeline, Typography } from 'antd';
import { useFetchData } from '../hooks';
import {
  TrackingChain as TrackingChainModel,
  TrackingDoc,
  TrackingStage,
} from '../types';
import StatusTag from './ui/StatusTag';
import { convertDate, dateFormat } from '../lib/utils';

const { Text } = Typography;

interface TrackingChainProps {
  api: string;
  id: string | undefined;
}

const stageLabel: Record<TrackingStage['stage'], string> = {
  BOM: 'BOM/Prod Order',
  PR: 'Purchase Request',
  CANVASS: 'Canvass',
  PO: 'Purchase Order',
  RR: 'Receiving Report',
  DR: 'Internal DR',
};

const moduleRoute: Record<string, (id: string) => string> = {
  BOM: (id) => `/production/bill-of-material/${id}`,
  PR: (id) => `/purchase-request/${id}`,
  CANVASS: (id) => `/purchase-canvass/${id}`,
  PO: (id) => `/purchase-order/${id}`,
  RR: (id) => `/stock-movement/receiving-report/${id}`,
  DR: (id) => `/stock-movement/delivery-receipt/${id}`,
};

const EdgeTag = ({ edge }: { edge: TrackingStage['edgeFromPrevious'] }) => {
  switch (edge) {
    case 'DERIVED':
      return <Tag>derived</Tag>;
    case 'INFERRED':
      return <Tag color={'orange'}>inferred</Tag>;
    case 'MISSING':
      return <Tag color={'red'}>gap</Tag>;
    default:
      return null;
  }
};

const TrackingDocView = ({
  doc,
  isAnchor,
}: {
  doc: TrackingDoc;
  isAnchor: boolean;
}) => {
  const route = doc.id !== null ? moduleRoute[doc.module] : undefined;
  return (
    <div className={'flex flex-wrap items-center gap-1'}>
      {isAnchor ? (
        <>
          <Text strong>{doc.code}</Text>
          <Tag color={'blue'}>this document</Tag>
        </>
      ) : route !== undefined ? (
        <Link to={route(doc.id as string)}>
          <span className={'text-blue-400 font-semibold'}>{doc.code}</span>
        </Link>
      ) : (
        <>
          <Text>{doc.code}</Text>
          <Tag color={'red'}>not found</Tag>
        </>
      )}
      {doc.status && <StatusTag status={doc.status} />}
      {doc.date && (
        <Text type={'secondary'} className={'text-xs'}>
          {convertDate(doc.date, dateFormat)}
        </Text>
      )}
    </div>
  );
};

/**
 * Cross-document chain (BOM -> PR -> Canvass -> PO -> RR -> DR) for whichever
 * document is open. Every stage always renders; edges that the backend could
 * only derive or infer are tagged as such rather than silently trusted.
 */
const TrackingChain: React.FC<TrackingChainProps> = ({ api, id }) => {
  const { data, isLoading, isError, error } = useFetchData<TrackingChainModel>(
    `${api}/${id}/tracking`,
  );

  // No id means no document, so no card. A FAILED call is different: the card
  // stays and says so -- vanishing on a 403/500 would be the same silent-gap
  // shape this feature exists against.
  if (id === undefined) return null;

  return (
    <Card title={'Request Tracking'} className={'w-full'}>
      {isLoading ? (
        <div className={'flex justify-center p-4'}>
          <Spin />
        </div>
      ) : isError || data === undefined ? (
        <Text type={'secondary'}>
          Tracking couldn&apos;t be loaded.
          {error?.response?.data?.message
            ? ` ${error.response.data.message}`
            : ''}
        </Text>
      ) : (
        <Timeline
          items={data.stages.map((stage, index) => ({
            key: stage.stage,
            color:
              stage.edgeFromPrevious === 'MISSING'
                ? 'red'
                : stage.documents.length > 0
                  ? 'blue'
                  : 'gray',
            children: (
              <div className={'space-y-1'}>
                <div className={'flex items-center gap-2'}>
                  <Text strong>{stageLabel[stage.stage]}</Text>
                  {index > 0 && <EdgeTag edge={stage.edgeFromPrevious} />}
                </div>
                {index > 0 && stage.edgeNote && stage.documents.length > 0 && (
                  <div>
                    <Text type={'secondary'} className={'text-xs'}>
                      {stage.edgeNote}
                    </Text>
                  </div>
                )}
                {stage.documents.length === 0 ? (
                  <Text type={'secondary'}>{stage.edgeNote ?? 'None'}</Text>
                ) : (
                  stage.documents.map((doc, docIndex) => (
                    <div
                      key={`${doc.module}-${doc.id ?? doc.code}-${docIndex}`}
                    >
                      <TrackingDocView
                        doc={doc}
                        isAnchor={
                          doc.module === data.anchor.module &&
                          doc.id !== null &&
                          doc.id === data.anchor.id
                        }
                      />
                      {doc.sourceText && (
                        <Text type={'secondary'} className={'text-xs'}>
                          source: &quot;{doc.sourceText}&quot;
                        </Text>
                      )}
                    </div>
                  ))
                )}
                {stage.overflowCount > 0 && (
                  <Text type={'secondary'}>+{stage.overflowCount} more</Text>
                )}
              </div>
            ),
          }))}
        />
      )}
    </Card>
  );
};

export default TrackingChain;
